import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetUserByIdParams,
  GetUserByIdData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetUserByIdState
  extends MergeRequestState<GetUserByIdData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getUserById,
  isFetching: (state: ReduxState) => state.server.getUserById.isFetching,
  data: (state: ReduxState) => state.server.getUserById.data,
  error: (state: ReduxState) => state.server.getUserById.error,
};

const initialState: GetUserByIdState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getUserById';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { params, config }: { params: GetUserByIdParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getUserById(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetUserById {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getUserById: ServerGetUserById = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
